import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; // useNavigate used in ProGate
import toast from "react-hot-toast";
import clsx from "clsx";
import { Trans, useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { DMTemplate, InstagramAccount } from "../api/types";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import Switch from "../components/ui/Switch";
import { useAuth } from "../store/auth";
import {
  IconBolt,
  IconChat,
  IconDM,
  IconHeart,
  IconPencil,
  IconPlus,
  IconTrash,
} from "../components/ui/Icons";

function ProGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10 ring-1 ring-brand-400/20">
        <IconDM size={32} className="text-brand-300" />
      </div>
      <h2 className="mb-2 text-xl font-semibold text-white">
        {t("dmTemplates.proGate.title")}
      </h2>
      <p className="mb-6 max-w-sm text-sm text-slate-400">
        {t("dmTemplates.proGate.subtitle")}
      </p>
      <button
        type="button"
        className="btn-primary"
        onClick={() => navigate("/settings")}
      >
        {t("dmTemplates.proGate.cta")}
      </button>
    </div>
  );
}

export default function DMTemplatesPage() {
  const { t } = useTranslation();
  const userPlan = useAuth((s) => s.user?.plan);
  const isPro = userPlan === "pro" || userPlan === "label";
  const qc = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
  });

  const templatesQuery = useQuery({
    queryKey: ["dm-templates"],
    queryFn: async () => {
      const { data } = await api.get<{ templates: DMTemplate[] }>("/api/dm-templates");
      return data.templates;
    },
  });

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<DMTemplate | null>(null);

  const createMutation = useMutation({
    mutationFn: async (body: any) => api.post("/api/dm-templates", body),
    onSuccess: () => {
      toast.success(t("dmTemplates.toast.created"));
      qc.invalidateQueries({ queryKey: ["dm-templates"] });
      setShowNew(false);
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) =>
      api.patch(`/api/dm-templates/${id}`, body),
    onSuccess: () => {
      toast.success(t("dmTemplates.toast.saved"));
      qc.invalidateQueries({ queryKey: ["dm-templates"] });
      setEditing(null);
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/dm-templates/${id}`),
    onSuccess: () => {
      toast.success(t("dmTemplates.toast.deleted"));
      qc.invalidateQueries({ queryKey: ["dm-templates"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const testSendMutation = useMutation({
    mutationFn: async ({ id, target }: { id: string; target: string }) =>
      api.post(`/api/dm-templates/${id}/test-send`, { targetUsername: target }),
    onSuccess: () => {
      toast.success(t("dmTemplates.toast.testEnqueued"));
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const templates = templatesQuery.data || [];
  const accounts = accountsQuery.data || [];

  if (!isPro) return <ProGate />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.dmTemplates")}
        title={t("dmTemplates.title")}
        subtitle={t("dmTemplates.subtitle")}
        icon={<IconDM />}
        actions={
          <button
            className="btn-primary w-full justify-center sm:w-auto"
            onClick={() => setShowNew(true)}
            disabled={accounts.length === 0 || showNew}
          >
            <IconPlus size={16} />
            {t("dmTemplates.new")}
          </button>
        }
      />

      <div className="rounded-xl border border-slate-800/70 bg-slate-900/40 p-4 text-sm text-slate-300">
        <Trans
          i18nKey="dmTemplates.help"
          values={{
            usernamePlaceholder: "{{username}}",
            firstNamePlaceholder: "{{first_name}}",
          }}
          components={{
            code: <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-brand-200" />,
            strong: <strong className="font-semibold text-white" />,
          }}
        />
      </div>

      {showNew && (
        <TemplateForm
          accounts={accounts}
          initial={null}
          onCancel={() => setShowNew(false)}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      )}

      {templates.length === 0 && !showNew ? (
        <EmptyState
          icon={<IconDM size={26} />}
          title={t("dmTemplates.emptyState.title")}
          description={t("dmTemplates.emptyState.subtitle")}
          action={
            accounts.length === 0 ? (
              <Link to="/instagram-accounts/new" className="btn-primary px-5 py-2.5">
                {t("dmTemplates.emptyState.needAccount")}
              </Link>
            ) : (
              <button className="btn-primary px-5 py-2.5" onClick={() => setShowNew(true)}>
                <IconPlus size={16} />
                {t("dmTemplates.emptyState.cta")}
              </button>
            )
          }
        />
      ) : (
        <div className="space-y-4">
          {templates.map((tpl) => {
            const acc = accounts.find((a) => a.id === tpl.igAccountId);
            return editing && editing._id === tpl._id ? (
              <TemplateForm
                key={tpl._id}
                accounts={accounts}
                initial={tpl}
                onCancel={() => setEditing(null)}
                onSubmit={(values) =>
                  updateMutation.mutate({ id: tpl._id, body: values })
                }
              />
            ) : (
              <TemplateCard
                key={tpl._id}
                template={tpl}
                account={acc}
                onEdit={() => setEditing(tpl)}
                onDelete={() => {
                  if (confirm(t("dmTemplates.confirmDelete", { name: tpl.name }))) {
                    deleteMutation.mutate(tpl._id);
                  }
                }}
                onTestSend={(target) =>
                  testSendMutation.mutate({ id: tpl._id, target })
                }
                testSendPending={testSendMutation.isPending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Template card
 *  Displays template metadata at the top plus a chat-bubble preview that mimics
 *  Instagram DMs - much more concrete than an unstyled pre block.
 * ------------------------------------------------------------------------ */

function TemplateCard({
  template: tpl,
  account,
  onEdit,
  onDelete,
  onTestSend,
  testSendPending,
}: {
  template: DMTemplate;
  account: InstagramAccount | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onTestSend: (target: string) => void;
  testSendPending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const displayName = account?.igUsername || t("common.unknown");
  const previewUser = "yourfan";
  const [testTarget, setTestTarget] = useState("");

  // Inline preview: replace placeholders so the bubble looks real.
  const previewBody = tpl.body
    .replace(/\{\{\s*username\s*\}\}/gi, previewUser)
    .replace(/\{\{\s*first_name\s*\}\}/gi, previewUser);

  const accountReady = account?.status === "active";
  const canTestSend = tpl.enabled && accountReady && testTarget.trim().length > 0;

  return (
    <article className="card space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {account ? (
            <Avatar name={account.igUsername} size="md" variant="instagram" />
          ) : (
            <Avatar name={tpl.name} size="md" />
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold tracking-tight text-white">{tpl.name}</h3>
              <span
                className={clsx(
                  "pill ring-1",
                  tpl.enabled
                    ? "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30"
                    : "bg-slate-500/20 text-slate-300 ring-slate-500/30"
                )}
              >
                {tpl.enabled ? t("common.enabled") : t("common.disabled")}
              </span>
              <span className="pill bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/30">
                {tpl.triggerType === "on_follow" ? (
                  <>
                    <IconBolt size={11} />
                    {t("dmTemplates.trigger.on_follow")}
                  </>
                ) : (
                  t("dmTemplates.trigger.manual")
                )}
              </span>
            </div>
            <p className="text-xs text-slate-400">@{displayName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5"
            onClick={onEdit}
            aria-label={t("common.edit")}
            title={t("common.edit")}
          >
            <IconPencil size={16} />
          </button>
          <button
            className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5 text-rose-300 hover:!bg-rose-500/10 hover:!text-rose-200"
            onClick={onDelete}
            aria-label={t("common.delete")}
            title={t("common.delete")}
          >
            <IconTrash size={16} />
          </button>
        </div>
      </header>

      {/* Chat-bubble preview */}
      <ChatPreview
        senderName={displayName}
        senderAvatar={
          account ? <Avatar name={account.igUsername} size="sm" variant="instagram" /> : null
        }
        body={previewBody}
      />

      {/* Test-send: lets the user verify the auto-DM pipeline end-to-end
          without waiting for a real new follower. The body is rendered
          server-side using the same path the watcher uses. */}
      <div className="rounded-xl border border-slate-800/60 bg-slate-950/30 p-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t("dmTemplates.testSend.title")}
        </p>
        <p className="mb-2 text-xs text-slate-400">
          {t("dmTemplates.testSend.help")}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={testTarget}
            onChange={(e) => setTestTarget(e.target.value.replace(/^@/, "").trim())}
            placeholder={t("dmTemplates.testSend.placeholder")}
            className="input flex-1"
            spellCheck={false}
            autoCapitalize="none"
            disabled={!tpl.enabled || !accountReady || testSendPending}
          />
          <button
            type="button"
            className="btn-secondary sm:w-auto"
            disabled={!canTestSend || testSendPending}
            onClick={() => onTestSend(testTarget)}
            title={
              !accountReady
                ? t("dmTemplates.testSend.accountInactive")
                : !tpl.enabled
                ? t("dmTemplates.testSend.templateDisabled")
                : undefined
            }
          >
            <IconBolt size={14} />
            {testSendPending
              ? t("dmTemplates.testSend.sending")
              : t("dmTemplates.testSend.cta")}
          </button>
        </div>
        {!accountReady && (
          <p className="mt-2 text-[11px] text-amber-300/80">
            {t("dmTemplates.testSend.accountInactive")}
          </p>
        )}
      </div>
    </article>
  );
}

function ChatPreview({
  senderName,
  senderAvatar,
  body,
}: {
  senderName: string;
  senderAvatar: JSX.Element | null;
  body: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-950/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {senderAvatar}
          <div className="text-xs">
            <div className="font-semibold text-slate-200">@{senderName}</div>
            <div className="text-[10px] text-slate-500">DM preview</div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          <IconHeart size={14} />
          <IconChat size={14} />
        </div>
      </div>
      <div className="flex flex-col items-start gap-1">
        <div className="relative max-w-[85%] rounded-2xl rounded-bl-md bg-gradient-to-br from-brand-500 to-fuchsia-600 px-3.5 py-2.5 text-sm text-white shadow-lg shadow-brand-900/30">
          <p className="whitespace-pre-wrap break-words">{body}</p>
        </div>
        <span className="text-[10px] text-slate-600">now</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Template form
 * ------------------------------------------------------------------------ */

interface FormProps {
  accounts: InstagramAccount[];
  initial: DMTemplate | null;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

function TemplateForm({ accounts, initial, onCancel, onSubmit }: FormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        igAccountId: initial.igAccountId,
        name: initial.name,
        body: initial.body,
        triggerType: initial.triggerType,
        enabled: initial.enabled,
      };
    }
    return {
      igAccountId: accounts[0]?.id || "",
      name: "",
      body: t("dmTemplates.form.defaultBody", { usernameVar: "{{username}}" }),
      triggerType: "on_follow" as const,
      enabled: true,
    };
  });
  const [previewUsername, setPreviewUsername] = useState("janedoe");
  const [preview, setPreview] = useState("");

  useEffect(() => {
    let cancel = false;
    api
      .post("/api/dm-templates/preview", { body: form.body, username: previewUsername })
      .then((r) => {
        if (!cancel) setPreview(r.data.preview);
      })
      .catch(() => {
        if (!cancel) setPreview("");
      });
    return () => {
      cancel = true;
    };
  }, [form.body, previewUsername]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (initial) {
      const { igAccountId: _ignored, ...rest } = form;
      onSubmit(rest);
    } else {
      onSubmit(form);
    }
  }

  const selectedAccount = accounts.find((a) => a.id === form.igAccountId);

  return (
    <form onSubmit={submit} className="card space-y-5 ring-1 ring-brand-400/20">
      <header className="space-y-1">
        <p className="section-eyebrow">{initial ? t("common.edit") : t("dmTemplates.new")}</p>
        <h3 className="text-lg font-semibold text-white">
          {initial
            ? t("dmTemplates.form.headingEdit", { name: initial.name })
            : t("dmTemplates.form.headingNew")}
        </h3>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {!initial && (
          <div>
            <label className="label">{t("dmTemplates.form.account")}</label>
            <select
              className="input"
              value={form.igAccountId}
              onChange={(e) => setForm((f) => ({ ...f, igAccountId: e.target.value }))}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  @{a.igUsername}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label">{t("dmTemplates.form.name")}</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <label className="label">{t("dmTemplates.form.trigger")}</label>
        <select
          className="input"
          value={form.triggerType}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              triggerType: e.target.value as "on_follow" | "manual",
            }))
          }
        >
          <option value="on_follow">{t("dmTemplates.trigger.on_follow")}</option>
          <option value="manual">{t("dmTemplates.trigger.manual")}</option>
        </select>
      </div>

      <div>
        <label className="label">{t("dmTemplates.form.message")}</label>
        <textarea
          required
          rows={5}
          maxLength={900}
          className="input font-mono"
          value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
        />
        <p className="helper">
          {form.body.length}/900 — {"{{username}}"}, {"{{first_name}}"}
        </p>
      </div>

      {/* Live preview */}
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="section-eyebrow">{t("dmTemplates.form.previewAs")}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">@</span>
            <input
              className="input w-36"
              value={previewUsername}
              onChange={(e) => setPreviewUsername(e.target.value)}
            />
          </div>
        </div>
        <ChatPreview
          senderName={selectedAccount?.igUsername || "yourstage"}
          senderAvatar={
            selectedAccount ? (
              <Avatar name={selectedAccount.igUsername} size="sm" variant="instagram" />
            ) : null
          }
          body={preview || form.body}
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-slate-800 pt-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={form.enabled}
            onChange={(next) => setForm((f) => ({ ...f, enabled: next }))}
            ariaLabel={t("common.enabled")}
            size="sm"
          />
          <span>{t("common.enabled")}</span>
        </label>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary">
            {initial ? t("dmTemplates.form.submitEdit") : t("dmTemplates.form.submitNew")}
          </button>
        </div>
      </div>
    </form>
  );
}
